import React from 'react';
type Tome = {
  number: string;
  title: string;
  range: string;
  framework: string;
  promise: string;
  badge: string;
  contents: string[];
};
const tomes: Tome[] = [{
  number: '01',
  title: 'Welcome to the Room',
  range: 'Activities 1–15',
  framework: 'The Do · Help · Feel Ladder',
  promise: 'Group starters and social games that bring every resident into the circle.',
  badge: 'bg-sage',
  contents: ['The Welcome Circle: greet every resident by name in under 10 minutes', 'Big-Card Bingo and Easy Team Trivia, where any answer counts', 'Coffee Club: turn a drink break into a 30-minute social group', 'The 60-Second Rung Check: place each resident for today’s activity', 'Busy vs. engaged: how to tell if the room is really with you', '10 five-minute social fillers with nothing to prepare']
}, {
  number: '02',
  title: 'Move Together',
  range: 'Activities 16–30',
  framework: 'The Seated-First Circle',
  promise: 'Seated movement, music and rhythm that every resident can join.',
  badge: 'bg-gold text-gold-ink',
  contents: ['Seated Wake-Up: a 10-minute warm-up you can lead from memory', 'Bucket Drum Circle and Shaker Songs from everyday objects', 'The Beat-Match Music Guide: choose music by tempo and era, not title', 'Decade Dial: a music-by-decade game where any guess counts', 'Movement Readiness Check: what to confirm with nursing first', 'Exercise class vs. movement moment: why short and playful works better']
}, {
  number: '03',
  title: 'Hands with Purpose',
  range: 'Activities 31–45',
  framework: 'The Real-Work Rule',
  promise: 'Real tasks, creative projects and kitchen and garden activities that feel like adult work.',
  badge: 'bg-smoke',
  contents: ['Laundry Folding Team: a folding crew with a real job to do', 'Table Setting Crew: set the dining room together before a meal', 'Watercolor Wash: wet-paper painting where every result looks good', 'Herb Pot Planting and Bread Dough Kneading', 'Set-Show-Share: the 3-step way to run any creative project', 'Safe prep vs. risky prep: what residents do and what staff handle first']
}, {
  number: '04',
  title: 'Share a Story',
  range: 'Activities 46–60',
  framework: 'The Open-Door Question',
  promise: 'Conversation, reminiscence and traditions without testing anyone’s memory.',
  badge: 'bg-sage',
  contents: ['My First Job: a work-stories circle for every background', 'Photo Talk: large everyday photos that start conversations', 'Object-Story-Echo: turn one object into a shared story', 'Around the World Table and Traditions Circle, for every culture and faith in the room', 'A family input form that invites families to share stories', 'Quiz vs. share: why “Do you remember?” closes the door']
}, {
  number: '05',
  title: 'Sensory Afternoons',
  range: 'Activities 61–75',
  framework: 'The Five-Sense Tray',
  promise: 'Sensory stations and low-demand activities for the late-day hours.',
  badge: 'bg-gold text-gold-ink',
  contents: ['Scent Jars and Texture Board', 'Hand Care Circle: warm towel and lotion, with consent', 'Nature Sounds: a 15-minute listening group you run from one phone', 'The Energy Curve: busier groups early, slower groups later in the day', 'Staff Handoff Note: tell the evening team what worked', 'Short and slow: pace a late-day group so no one is rushed']
}, {
  number: '06',
  title: 'Room to Room',
  range: 'Activities 76–90',
  framework: 'The 10-Minute Visit',
  promise: 'One-to-one visits, bedside activities and small moments across the day.',
  badge: 'bg-smoke',
  contents: ['Photo Album Visit and Music at the Bedside', 'Hand and Name Visit: a greeting ritual for residents who rarely open their eyes', 'Favorite Things Box for each resident’s room', 'Weekly 1:1 rotation: fit every in-room resident into the week', 'Two-minute moments in hallways, dining rooms and doorways', 'Family Visit Helper: simple activities families can do on their visits']
}];
export function Tomes() {
  return <section aria-labelledby="tomes-heading" className="w-full bg-cream" data-id="element-282" data-mp="3h5xd03a3azuq">
      <div className="max-w-6xl mx-auto px-5 py-16 sm:py-20" data-id="element-283" data-mp="3uxu361s76zoh">
        <div className="text-center" data-id="element-284" data-mp="3v0g8durm933j">
          <p className="text-[11px] font-semibold uppercase tracking-[1.8px] text-gold-deep" data-id="element-285" data-mp="3pzsd2wwcu03i">
            From the room to the bedside
          </p>
          <h2 id="tomes-heading" className="mt-3 font-display font-bold text-plum text-3xl sm:text-5xl" data-id="element-286" data-mp="8qdnnfcji1ky">
            Six volumes. <span className="italic" data-id="element-287" data-mp="2ahqitcopgv3t">Ninety activities.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-ink-soft" data-id="element-288" data-mp="25jozcqndatrt">
            The path runs from the social group to the resident who no longer
            reaches the group. Each volume stands on its own: pages 2 to 4
            explain the whole system, so you can start with any of them.
          </p>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-289" data-mp="bcczf5ni6ib3">
          {tomes.map(tome => <article key={tome.number} className="flex flex-col rounded-3xl border border-cream-dark bg-white p-6 shadow-card" data-id="element-290" data-mp="g9mr6ryd7623">
              <div className="flex items-center gap-2.5" data-id="element-291" data-mp="6c8hdb6y5fir">
                <span className={`flex h-9 w-9 items-center justify-center rounded-full font-display text-base font-bold ${tome.badge.includes('text-') ? '' : 'text-white'} ${tome.badge}`} data-id="element-292" data-mp="6dtcb1x6b0h5">
                  {tome.number}
                </span>
                <span className="text-[11px] font-semibold uppercase tracking-widest text-ink-soft" data-id="element-293" data-mp="1rptgmxovjfj9">
                  Volume {tome.number} · {tome.range}
                </span>
              </div>
              <h3 className="mt-3 font-display text-2xl font-bold leading-snug text-plum" data-id="element-294" data-mp="1q6bekr4i8igo">
                {tome.title}
              </h3>
              <p className="mt-1 text-[12px] font-semibold uppercase tracking-wider text-gold-deep" data-id="element-295" data-mp="1g9ouw5nwzxg8">
                {tome.framework}
              </p>
              <p className="mt-2 text-sm text-ink-soft" data-id="element-296" data-mp="1bdrn4nqpvzf2">{tome.promise}</p>
              <ul className="mt-4 grid gap-2 border-t border-cream-dark pt-4" data-id="element-297" data-mp="28ymce6kaxq7x">
                {tome.contents.map(item => <li key={item} className="flex gap-2 text-[14px] text-ink" data-id="element-298" data-mp="242f4474fo2mt">
                    <span className="mt-[8px] h-1.5 w-1.5 flex-none rounded-full bg-gold" data-id="element-299" data-mp="1z7jbp5vabd96" />
                    {item}
                  </li>)}
              </ul>
            </article>)}
        </div>
      </div>
    </section>;
}